import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Stethoscope, Plus, Calendar, Thermometer, Pill, Upload, FileText, X, Printer, Activity, Heart, User, Trash2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Badge } from './ui/badge';
import { QRCodeSVG } from 'qrcode.react';
import axios from 'axios';
import { toast } from 'sonner';
import { useViewOnly } from '../contexts/ViewOnlyContext';
import { ensureDrPrefix } from '../lib/utils';
import { calculateBMI, getBMIBadgeClasses } from '../utils/bmiCalculator';
import MedicationIcon from './MedicationIcon';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper function to open S3 files with presigned URLs
const openFileWithPresignedUrl = async (fileUrl) => {
  if (!fileUrl) {
    console.error('No file URL provided');
    return;
  }
  
  console.log('Opening file:', fileUrl);
  
  // Check if it's an S3 URL
  if (fileUrl.includes('s3.') && fileUrl.includes('amazonaws.com')) {
    try {
      // Extract S3 key from URL
      const urlParts = fileUrl.split('.amazonaws.com/');
      if (urlParts.length > 1) {
        const s3Key = urlParts[1].split('?')[0]; // Remove any query params
        console.log('Fetching presigned URL for S3 key:', s3Key);
        // Get presigned URL from backend
        const response = await axios.get(`${API}/s3-file/${s3Key}`);
        if (response.data && response.data.url) {
          window.open(response.data.url, '_blank');
          return;
        }
      }
    } catch (error) {
      console.error('Error getting presigned URL:', error);
    }
  }
  
  // For local files or fallback, open directly
  let url;
  if (fileUrl.startsWith('http')) {
    url = fileUrl;
  } else if (fileUrl.startsWith('/api/')) {
    url = `${BACKEND_URL}${fileUrl}`;
  } else {
    url = `${BACKEND_URL}${fileUrl}`;
  }
  
  console.log('Opening URL:', url);
  window.open(url, '_blank');
};

const MedicalSessions = ({ baby }) => {
  const navigate = useNavigate();
  // Always read-only for baby/parent users - only doctors can edit
  const viewOnlyMode = true;
  const [sessions, setSessions] = useState([]);
  const [filteredSessions, setFilteredSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingSession, setEditingSession] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadingDocs, setUploadingDocs] = useState(false);
  const [showAttachReportsDialog, setShowAttachReportsDialog] = useState(false);
  const [attachReportsFiles, setAttachReportsFiles] = useState([]);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [viewSession, setViewSession] = useState(null);
  const [showPreviousSessionDialog, setShowPreviousSessionDialog] = useState(false);
  const [previousSessionData, setPreviousSessionData] = useState(null);
  const [followUpSessionId, setFollowUpSessionId] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState('all');
  const [selectedYear, setSelectedYear] = useState('all');
  const [doctorSearchQuery, setDoctorSearchQuery] = useState('');
  const [showPrintPreview, setShowPrintPreview] = useState(false);
  const [printSession, setPrintSession] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteDocumentData, setDeleteDocumentData] = useState(null);
  const [sessionReports, setSessionReports] = useState({});
  const [formData, setFormData] = useState({
    session_date: '',
    doctor_name: '',
    symptoms: '',
    diagnosis: '',
    medications: [{ name: '', timing: 'Before Food', duration: '', time_of_day: [] }],
    temperature: '',
    duration: '',
    notes: '',
    follow_up_of: ''
  });
  const [submitting, setSubmitting] = useState(false);

  // Handle date input with auto-advance
  const handleDateInput = (e) => {
    let value = e.target.value;
    
    // Remove all non-numeric characters except /
    value = value.replace(/[^\d/]/g, '');
    
    // Auto-add slashes and format
    if (value.length === 2 && !value.includes('/')) {
      value = value + '/';
    } else if (value.length === 5 && value.split('/').length === 2) {
      value = value + '/';
    }
    
    // Limit to dd/mm/yyyy format (10 characters)
    if (value.length > 10) {
      value = value.substring(0, 10);
    }
    
    setFormData(prev => ({ ...prev, session_date: value }));
  };

  // Handle backspace for natural deletion
  const handleDateKeyDown = (e) => {
    if (e.key === 'Backspace') {
      const input = e.target;
      const value = input.value;
      const cursorPosition = input.selectionStart;
      
      // If cursor is right after a slash, delete the slash and the character before it
      if (cursorPosition > 0 && value[cursorPosition - 1] === '/') {
        e.preventDefault();
        const newValue = value.substring(0, cursorPosition - 2) + value.substring(cursorPosition);
        setFormData(prev => ({ ...prev, session_date: newValue }));
        // Set cursor position after the state updates
        setTimeout(() => {
          input.selectionStart = input.selectionEnd = cursorPosition - 2;
        }, 0);
      }
    }
  };

  // Calculate BMI for a session
  const getSessionBMI = (session, baby) => {
    if (session.height && session.weight && baby?.date_of_birth && baby?.gender) {
      return calculateBMI(
        parseFloat(session.weight),
        parseFloat(session.height),
        baby.date_of_birth,
        baby.gender,
        '' // No doctor specialization context in baby app
      );
    }
    return null;
  };

  const medicationTimings = [
    'Before Food',
    'After Food'
  ];

  useEffect(() => {
    fetchSessions();
  }, [baby]);

  // Fetch reports for all sessions (matching Doctor Dashboard behavior)
  useEffect(() => {
    const fetchReportsForSessions = async () => {
      if (!sessions || sessions.length === 0) {
        return;
      }
      
      const reportsMap = {};
      
      for (const session of sessions) {
        try {
          const response = await axios.get(`${API}/api/report-uploads/session/${session.id}`);
          if (response.data && response.data.length > 0) {
            reportsMap[session.id] = response.data;
          }
        } catch (error) {
          console.error(`Error fetching reports for session ${session.id}:`, error);
        }
      }
      
      setSessionReports(reportsMap);
    };
    
    if (sessions && sessions.length > 0) {
      fetchReportsForSessions();
    }
  }, [sessions]);

  // Filter sessions when sessions, month, year, or doctor search changes
  useEffect(() => {
    if (sessions.length === 0) {
      setFilteredSessions([]);
      return;
    }

    let filtered = [...sessions];

    // Filter by month and year
    if (selectedMonth !== 'all' || selectedYear !== 'all') {
      filtered = filtered.filter(session => {
        // Handle both DD/MM/YYYY and YYYY-MM-DD formats
        let sessionDate;
        if (session.session_date.includes('/')) {
          // DD/MM/YYYY format
          const [day, month, year] = session.session_date.split('/');
          sessionDate = new Date(year, month - 1, day);
        } else {
          // YYYY-MM-DD format
          sessionDate = new Date(session.session_date);
        }
        
        const sessionMonth = sessionDate.getMonth() + 1; // 1-12
        const sessionYear = sessionDate.getFullYear();

        const monthMatch = selectedMonth === 'all' || parseInt(selectedMonth) === sessionMonth;
        const yearMatch = selectedYear === 'all' || parseInt(selectedYear) === sessionYear;

        return monthMatch && yearMatch;
      });
    }

    // Filter by doctor name
    if (doctorSearchQuery.trim()) {
      filtered = filtered.filter(session => {
        const doctorName = session.doctor_name || session.updated_by || '';
        return doctorName.toLowerCase().includes(doctorSearchQuery.toLowerCase());
      });
      
      // Re-sort filtered sessions to maintain descending order
      filtered.sort((a, b) => {
        let dateA, dateB;
        if (a.session_date.includes('/')) {
          const [dayA, monthA, yearA] = a.session_date.split('/');
          dateA = new Date(yearA, monthA - 1, dayA);
        } else {
          dateA = new Date(a.session_date);
        }
        if (b.session_date.includes('/')) {
          const [dayB, monthB, yearB] = b.session_date.split('/');
          dateB = new Date(yearB, monthB - 1, dayB);
        } else {
          dateB = new Date(b.session_date);
        }
        
        // First sort by date
        const dateDiff = dateB - dateA;
        if (dateDiff !== 0) {
          return dateDiff; // Descending order (newest first)
        }
        
        // If same date, sort by created_at (most recently created first)
        if (a.created_at && b.created_at) {
          return new Date(b.created_at) - new Date(a.created_at);
        }
        
        return 0;
      });
    }

    setFilteredSessions(filtered);
  }, [sessions, selectedMonth, selectedYear, doctorSearchQuery]);

  const fetchSessions = async () => {
    try {
      const response = await axios.get(`${API}/medical-sessions/${baby.id}`);
      // Sort sessions in descending order (newest first)
      const sortedSessions = response.data.sort((a, b) => {
        let dateA, dateB;
        // Handle both DD/MM/YYYY and YYYY-MM-DD formats
        if (a.session_date.includes('/')) {
          const [dayA, monthA, yearA] = a.session_date.split('/');
          dateA = new Date(yearA, monthA - 1, dayA);
        } else {
          dateA = new Date(a.session_date);
        }
        if (b.session_date.includes('/')) {
          const [dayB, monthB, yearB] = b.session_date.split('/');
          dateB = new Date(yearB, monthB - 1, dayB);
        } else {
          dateB = new Date(b.session_date);
        }
        
        // First sort by date
        const dateDiff = dateB - dateA;
        if (dateDiff !== 0) {
          return dateDiff; // Descending order (newest first)
        }
        
        // If same date, sort by created_at (most recently created first)
        if (a.created_at && b.created_at) {
          return new Date(b.created_at) - new Date(a.created_at);
        }
        
        return 0;
      });
      setSessions(sortedSessions);
    } catch (error) {
      console.error('Error fetching medical sessions:', error);
      toast.error('Failed to load medical sessions');
    } finally {
      setLoading(false);
    }
  };

  const handlePrintPreview = (session) => {
    setPrintSession(session);
    setShowPrintPreview(true);
  };

  const handlePrint = () => {
    window.print();
  };

  const handleAddSession = async (e) => {
    e.preventDefault();
    
    if (!formData.session_date || !formData.symptoms || !formData.doctor_name) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      // Format medications as a string with timing, time_of_day, and duration
      const medicationsText = formData.medications
        .filter(med => med.name.trim())
        .map(med => {
          let medStr = med.name;
          if (med.timing) medStr += ` (${med.timing})`;
          if (med.time_of_day && med.time_of_day.length > 0) {
            medStr += ` - ${med.time_of_day.join(', ')}`;
          }
          if (med.duration) medStr += ` for ${med.duration}`;
          return medStr;
        })
        .join(' | '); // Use pipe separator to avoid confusion with commas in time_of_day
      
      const sessionData = {
        baby_id: baby.id,
        session_date: formData.session_date,
        doctor_name: formData.doctor_name,
        symptoms: formData.symptoms,
        diagnosis: formData.diagnosis,
        medication: medicationsText || null,
        temperature: formData.temperature ? parseFloat(formData.temperature) : null,
        notes: formData.notes || null,
        follow_up_of: formData.follow_up_of || null,
        documents: []
      };
      
      const response = await axios.post(`${API}/medical-sessions`, sessionData);
      const createdSession = response.data;
      
      // Upload documents if any were selected
      if (selectedFiles.length > 0) {
        await handleUploadDocuments(createdSession.id);
      }
      
      toast.success('Medical session added successfully!');
      setShowAddDialog(false);
      setFormData({
        session_date: new Date().toISOString().split('T')[0],
        doctor_name: '',
        symptoms: '',
        diagnosis: '',
        medications: [{ name: '', timing: 'Before Food', duration: '', time_of_day: [] }],
        temperature: '',
        duration: '',
        notes: '',
        follow_up_of: ''
      });
      setSelectedFiles([]);
      setFollowUpSessionId(null);
      fetchSessions();
    } catch (error) {
      console.error('Error adding medical session:', error);
      toast.error('Failed to add medical session');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteSession = async (sessionId) => {
    if (!window.confirm('Are you sure you want to delete this medical session?')) {
      return;
    }

    try {
      await axios.delete(`${API}/medical-sessions/${sessionId}`);
      toast.success('Medical session deleted successfully!');
      fetchSessions();
    } catch (error) {
      console.error('Error deleting medical session:', error);
      
      // Check if it's a 403 error (doctor-created session)
      if (error.response && error.response.status === 403) {
        toast.error('Cannot delete doctor-created sessions. Only doctors can delete their own sessions.');
      } else {
        toast.error('Failed to delete medical session');
      }
    }
  };

  // Handle uploading documents to existing session
  const handleUploadToExistingSession = async (sessionId, event) => {
    const files = Array.from(event.target.files);
    const validFiles = files.filter(file => {
      const extension = file.name.split('.').pop().toLowerCase();
      return ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'bmp'].includes(extension);
    });
    
    if (validFiles.length === 0) {
      toast.error('No valid files selected. Only PDF and image files are allowed.');
      return;
    }

    try {
      const formData = new FormData();
      validFiles.forEach(file => {
        formData.append('files', file);
      });

      await axios.post(`${API}/medical-sessions/${sessionId}/upload-documents`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      toast.success(`${validFiles.length} document(s) uploaded successfully!`);
      fetchSessions();
    } catch (error) {
      console.error('Error uploading documents:', error);
      toast.error('Failed to upload documents');
    }
  };

  const handleEditClick = (session) => {
    setEditingSession(session);
    
    // Parse medications from the medication string
    let medicationsList = [{ name: '', timing: 'Before Food', duration: '', time_of_day: [] }];
    if (session.medication) {
      // Parse medication string like "Paracetamol (After Food) - Morning, Night for 7 days | Med2..."
      // Split by pipe separator to handle multiple medications
      medicationsList = session.medication.split(' | ').map(med => {
        const trimmedMed = med.trim();
        
        // Initialize medication object
        let medObj = { name: '', timing: 'Before Food', duration: '', time_of_day: [] };
        
        // Extract duration if present (e.g., "for 7 days")
        let medWithoutDuration = trimmedMed;
        const durationMatch = trimmedMed.match(/\s+for\s+(.+)$/i);
        if (durationMatch) {
          medObj.duration = durationMatch[1].trim();
          medWithoutDuration = trimmedMed.substring(0, durationMatch.index);
        }
        
        // Extract time_of_day if present (e.g., "- Morning, Night")
        let medWithoutTime = medWithoutDuration;
        const timeMatch = medWithoutDuration.match(/\s+-\s+(.+)$/);
        if (timeMatch) {
          const timeStr = timeMatch[1].trim();
          medObj.time_of_day = timeStr.split(',').map(t => t.trim()).filter(t => t);
          medWithoutTime = medWithoutDuration.substring(0, timeMatch.index);
        }
        
        // Extract name and timing (e.g., "Paracetamol (After Food)")
        const nameTimingMatch = medWithoutTime.match(/^(.+?)\s*\((.+?)\)$/);
        if (nameTimingMatch) {
          medObj.name = nameTimingMatch[1].trim();
          medObj.timing = nameTimingMatch[2].trim();
        } else {
          medObj.name = medWithoutTime.trim();
        }
        
        return medObj;
      });
    }
    
    setFormData({
      session_date: session.session_date,
      doctor_name: session.doctor_name,
      symptoms: session.symptoms,
      diagnosis: session.diagnosis || '',
      medications: medicationsList,
      temperature: session.temperature ? session.temperature.toString() : '',
      duration: session.duration || '',
      notes: session.notes || '',
      follow_up_of: session.follow_up_of || ''
    });
    
    setShowEditDialog(true);
  };

  const handleUpdateSession = async (e) => {
    e.preventDefault();
    
    if (!formData.session_date || !formData.symptoms || !formData.doctor_name) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      // Format medications as a string with timing, time_of_day, and duration
      const medicationsText = formData.medications
        .filter(med => med.name.trim())
        .map(med => {
          let medStr = med.name;
          if (med.timing) medStr += ` (${med.timing})`;
          if (med.time_of_day && med.time_of_day.length > 0) {
            medStr += ` - ${med.time_of_day.join(', ')}`;
          }
          if (med.duration) medStr += ` for ${med.duration}`;
          return medStr;
        })
        .join(' | '); // Use pipe separator to avoid confusion with commas in time_of_day
      
      const sessionData = {
        baby_id: baby.id,
        session_date: formData.session_date,
        doctor_name: formData.doctor_name,
        symptoms: formData.symptoms,
        diagnosis: formData.diagnosis,
        medication: medicationsText || null,
        temperature: formData.temperature ? parseFloat(formData.temperature) : null,
        notes: formData.notes || null,
        follow_up_of: formData.follow_up_of || null
      };
      
      await axios.put(`${API}/medical-sessions/${editingSession.id}`, sessionData);
      toast.success('Medical session updated successfully!');
      setShowEditDialog(false);
      setEditingSession(null);
      setFormData({
        session_date: '',
        doctor_name: '',
        symptoms: '',
        diagnosis: '',
        medications: [{ name: '', timing: 'Before Food', duration: '', time_of_day: [] }],
        temperature: '',
        duration: '',
        notes: '',
        follow_up_of: ''
      });
      fetchSessions();
    } catch (error) {
      console.error('Error updating medical session:', error);
      toast.error('Failed to update medical session');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddMedication = () => {
    setFormData(prev => ({
      ...prev,
      medications: [...prev.medications, { name: '', timing: 'As Needed', frequency: '' }]
    }));
  };

  const handleRemoveMedication = (index) => {
    setFormData(prev => ({
      ...prev,
      medications: prev.medications.filter((_, i) => i !== index)
    }));
  };

  const handleMedicationChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      medications: prev.medications.map((med, i) => 
        i === index ? { ...med, [field]: value } : med
      )
    }));
  };

  const formatDate = (dateString) => {
    // If already in DD/MM/YYYY format, return as is
    if (dateString && dateString.includes('/')) {
      return dateString;
    }
    // Otherwise convert from ISO format
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Get previous session details by ID
  const getSessionById = (sessionId) => {
    return sessions.find(s => s.id === sessionId);
  };

  // Get complete follow-up chain for a session
  const getFollowUpChain = (sessionId) => {
    const chain = [];
    let currentId = sessionId;
    
    while (currentId) {
      const session = getSessionById(currentId);
      if (session) {
        chain.push(session);
        currentId = session.follow_up_of;
      } else {
        break;
      }
    }
    
    return chain;
  };

  // Handle file selection
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const maxSize = 10 * 1024 * 1024; // 10MB in bytes
    
    const validFiles = files.filter(file => {
      const extension = file.name.split('.').pop().toLowerCase();
      const isValidType = ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'bmp', 'dcm', 'dicom'].includes(extension);
      const isValidSize = file.size <= maxSize;
      
      if (!isValidType) {
        toast.error(`${file.name}: File type not allowed. Only PDF, images, and DICOM files are accepted.`);
        return false;
      }
      
      if (!isValidSize) {
        toast.error(`${file.name}: File too large. Maximum size is 10MB.`);
        return false;
      }
      
      return true;
    });
    
    setSelectedFiles(prev => [...prev, ...validFiles]);
  };

  // Remove selected file
  const handleRemoveFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Handle file select for attach reports dialog
  const handleAttachReportsFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const maxSize = 10 * 1024 * 1024; // 10MB in bytes
    
    const validFiles = files.filter(file => {
      const extension = file.name.split('.').pop().toLowerCase();
      const isValidType = ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'bmp', 'dcm', 'dicom'].includes(extension);
      const isValidSize = file.size <= maxSize;
      
      if (!isValidType) {
        toast.error(`${file.name}: File type not allowed. Only PDF, images, and DICOM files are accepted.`);
        return false;
      }
      
      if (!isValidSize) {
        toast.error(`${file.name}: File too large. Maximum size is 10MB.`);
        return false;
      }
      
      return true;
    });
    
    setAttachReportsFiles(prev => [...prev, ...validFiles]);
  };

  // Remove file from attach reports dialog
  const handleRemoveAttachReportFile = (index) => {
    setAttachReportsFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Upload reports to doctor-created session
  const handleAttachReports = async () => {
    if (attachReportsFiles.length === 0) {
      toast.error('Please select at least one file to upload.');
      return;
    }

    if (!editingSession || !editingSession.id) {
      toast.error('Session not found.');
      return;
    }

    setUploadingDocs(true);
    try {
      const formData = new FormData();
      attachReportsFiles.forEach(file => {
        formData.append('files', file);
      });

      await axios.post(`${API}/medical-sessions/${editingSession.id}/upload-documents`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      toast.success(`${attachReportsFiles.length} report(s) attached successfully!`);
      setShowAttachReportsDialog(false);
      setAttachReportsFiles([]);
      setEditingSession(null);
      fetchSessions();
    } catch (error) {
      console.error('Error attaching reports:', error);
      toast.error('Failed to attach reports. Please try again.');
    } finally {
      setUploadingDocs(false);
    }
  };

  // Upload documents for a session
  const handleUploadDocuments = async (sessionId) => {
    if (selectedFiles.length === 0) {
      return;
    }

    setUploadingDocs(true);
    try {
      const formData = new FormData();
      selectedFiles.forEach(file => {
        formData.append('files', file);
      });

      await axios.post(`${API}/medical-sessions/${sessionId}/upload-documents`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      toast.success(`${selectedFiles.length} document(s) uploaded successfully!`);
      setSelectedFiles([]);
      fetchSessions();
    } catch (error) {
      console.error('Error uploading documents:', error);
      toast.error('Failed to upload documents');
    } finally {
      setUploadingDocs(false);
    }
  };

  // Delete document handlers
  const handleDeleteDocumentClick = (sessionId, docIndex, doc) => {
    setDeleteDocumentData({ sessionId, docIndex, doc });
    setShowDeleteConfirm(true);
  };

  const handleConfirmDeleteDocument = async () => {
    if (!deleteDocumentData) return;

    const { sessionId, docIndex } = deleteDocumentData;
    
    try {
      await axios.delete(
        `${API}/medical-sessions/${sessionId}/documents/${docIndex}?uploaded_by=parent`
      );
      
      toast.success('Document deleted successfully!');
      setShowDeleteConfirm(false);
      setDeleteDocumentData(null);
      fetchSessions(); // Refresh sessions to show updated document list
    } catch (error) {
      console.error('Error deleting document:', error);
      if (error.response?.status === 403) {
        toast.error('You can only delete documents you uploaded');
      } else {
        toast.error('Failed to delete document');
      }
    }
  };

  const handleCancelDeleteDocument = () => {
    setShowDeleteConfirm(false);
    setDeleteDocumentData(null);
  };


  // View previous session
  const handleViewPreviousSession = (sessionId) => {
    const session = getSessionById(sessionId);
    if (session) {
      setPreviousSessionData(session);
      setShowPreviousSessionDialog(true);
    }
  };

  // Create follow-up session
  const handleCreateFollowUpSession = (sessionId) => {
    setFormData({
      session_date: '',
      doctor_name: '',
      symptoms: '',
      diagnosis: '',
      medications: [{ name: '', timing: 'Before Food', duration: '', time_of_day: [] }],
      temperature: '',
      duration: '',
      notes: '',
      follow_up_of: sessionId
    });
    setFollowUpSessionId(sessionId);
    setShowAddDialog(true);
  };

  // Get file icon based on extension
  const getFileIcon = (url) => {
    if (!url) return '📄'; // Default icon if URL is null/undefined
    const extension = url.split('.').pop().toLowerCase();
    if (extension === 'pdf') {
      return '📄';
    }
    if (extension === 'dcm' || extension === 'dicom') {
      return '🔬';
    }
    return '🖼️';
  };

  // Get filename from document (handles both string URLs and objects with filename)
  const getFilename = (doc) => {
    // If doc is an object with filename property, use it
    if (typeof doc === 'object' && doc.filename) {
      return doc.filename;
    }
    // Otherwise treat as URL string and extract filename
    const url = typeof doc === 'string' ? doc : doc.url;
    if (!url) return 'Unknown file'; // Handle null/undefined URL
    return url.split('/').pop();
  };

  // Get document URL (handles both string URLs and objects)
  const getDocumentUrl = (doc) => {
    if (!doc) return null; // Handle null/undefined document
    return typeof doc === 'string' ? doc : (doc.url || null);
  };

  // Check if file is DICOM (for icon display)
  const isDICOMFile = (url) => {
    if (!url) return false; // Return false if URL is null/undefined
    const extension = url.split('.').pop().toLowerCase();
    return extension === 'dcm' || extension === 'dicom';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading medical sessions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="dashboard-header">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate('/')}
              className="text-white hover:bg-white hover:bg-opacity-20"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
                <Stethoscope className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold" style={{fontFamily: 'Space Grotesk'}}>Medical Sessions</h1>
                <p className="text-blue-200 text-sm">Track medical consultations and diagnosis</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="px-6 py-6">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900" style={{fontFamily: 'Space Grotesk'}}>
              Medical Sessions History
            </h3>
            {!viewOnlyMode && (
            <Button 
              onClick={() => setShowAddDialog(true)}
              className="bg-gray-900 hover:bg-gray-800 text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Session
            </Button>
            )}
          </div>
          
          {/* Month, Year, and Doctor Name Filter */}
          {sessions.length > 0 && (
            <div className="px-6 py-4 bg-gray-50 border-b">
              <div className="flex items-center gap-4 flex-wrap">
                <Label className="text-sm font-medium text-gray-700">Filter by:</Label>
                <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Month" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Months</SelectItem>
                    <SelectItem value="1">January</SelectItem>
                    <SelectItem value="2">February</SelectItem>
                    <SelectItem value="3">March</SelectItem>
                    <SelectItem value="4">April</SelectItem>
                    <SelectItem value="5">May</SelectItem>
                    <SelectItem value="6">June</SelectItem>
                    <SelectItem value="7">July</SelectItem>
                    <SelectItem value="8">August</SelectItem>
                    <SelectItem value="9">September</SelectItem>
                    <SelectItem value="10">October</SelectItem>
                    <SelectItem value="11">November</SelectItem>
                    <SelectItem value="12">December</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={selectedYear} onValueChange={setSelectedYear}>
                  <SelectTrigger className="w-32">
                    <SelectValue placeholder="Year" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Years</SelectItem>
                    {Array.from(new Set(sessions.map(s => {
                      // Handle both DD/MM/YYYY and YYYY-MM-DD formats
                      if (s.session_date.includes('/')) {
                        const [day, month, year] = s.session_date.split('/');
                        return parseInt(year);
                      } else {
                        return new Date(s.session_date).getFullYear();
                      }
                    })))
                      .filter(year => !isNaN(year) && year > 1900)
                      .sort((a, b) => b - a)
                      .map(year => (
                        <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                <div className="relative flex-1 max-w-xs">
                  <Input
                    type="text"
                    placeholder="Search by doctor name..."
                    value={doctorSearchQuery}
                    onChange={(e) => setDoctorSearchQuery(e.target.value)}
                    className="pl-9"
                  />
                  <User className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                </div>
                {(selectedMonth !== 'all' || selectedYear !== 'all' || doctorSearchQuery.trim()) && (
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      setSelectedMonth('all');
                      setSelectedYear('all');
                      setDoctorSearchQuery('');
                    }}
                  >
                    Clear Filters
                  </Button>
                )}
                <span className="text-sm text-gray-600 ml-auto">
                  Showing {filteredSessions.length} of {sessions.length} sessions
                </span>
              </div>
            </div>
          )}
          
          <div className="p-6">
            {sessions.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Stethoscope className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-lg font-medium">No medical sessions recorded</p>
                <p className="text-sm mt-1">Add sessions to track {baby.name}'s medical consultations</p>
              </div>
            ) : filteredSessions.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-lg font-medium">No sessions found for selected period</p>
                <p className="text-sm mt-1">Try adjusting the filters</p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredSessions.map((session) => (
                  <div 
                    key={session.id} 
                    className="border border-gray-200 rounded-lg p-5 hover:bg-gray-50 transition-colors cursor-pointer"
                    onClick={() => {
                      setViewSession(session);
                      setShowViewDialog(true);
                    }}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                          <Calendar className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900">{formatDate(session.session_date)}</p>
                          <p className="text-sm text-gray-600">{session.doctor_name || 'Parent'}</p>
                          {session.updated_by && (
                            <Badge variant="outline" className="bg-purple-50 text-purple-600 border-purple-200 text-xs mt-1">
                              by {ensureDrPrefix(session.formatted_doctor_name || session.doctor_name)}
                            </Badge>
                          )}
                          
                          {/* Follow-up chain display */}
                          {session.follow_up_of && (() => {
                            const followUpChain = getFollowUpChain(session.follow_up_of);
                            return (
                              <div className="mt-1 space-y-1">
                                {followUpChain.length === 1 ? (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setViewSession(followUpChain[0]);
                                      setShowViewDialog(true);
                                    }}
                                    className="flex items-center space-x-1 text-sm text-purple-600 hover:text-purple-800 hover:underline cursor-pointer"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                    </svg>
                                    <span>Follow-up to {formatDate(followUpChain[0].session_date)}</span>
                                  </button>
                                ) : (
                                  <div className="space-y-1">
                                    <div className="flex items-center space-x-1 text-sm text-purple-600">
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                      </svg>
                                      <span className="font-medium">Follow-up chain ({followUpChain.length} previous sessions):</span>
                                    </div>
                                    <div className="ml-5 space-y-1">
                                      {followUpChain.map((prevSession, index) => (
                                        <button
                                          key={prevSession.id}
                                          type="button"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setViewSession(prevSession);
                                            setShowViewDialog(true);
                                          }}
                                          className="flex items-center space-x-2 text-xs text-purple-600 hover:text-purple-800 hover:underline cursor-pointer"
                                        >
                                          <span className="font-semibold">#{followUpChain.length - index}</span>
                                          <span>{formatDate(prevSession.session_date)} - {prevSession.doctor_name || 'Doctor'}</span>
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                      {!viewOnlyMode && (
                        <div className="flex space-x-2">
                          {/* Only show Edit/Delete for parent-created sessions */}
                          {!session.updated_by && (
                            <>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditClick(session);
                                }}
                                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                title="Edit session"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteSession(session.id);
                                }}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                title="Delete session"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </Button>
                            </>
                          )}
                          
                          {/* Show Print button for doctor-created sessions */}
                          {session.updated_by && (
                            <>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handlePrintPreview(session);
                                }}
                                className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                                title="Print Prescription"
                              >
                                <Printer className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                    
                    {/* Expanded view - same format as doctor dashboard */}
                    <div className="space-y-3 ml-13">
                      {/* Symptoms */}
                      {session.symptoms && (
                        <div>
                          <h4 className="text-xs font-medium text-gray-500 uppercase mb-1">Symptoms</h4>
                          <p className="text-sm text-gray-800 leading-relaxed">{session.symptoms}</p>
                        </div>
                      )}
                      
                      {/* Diagnosis */}
                      {session.diagnosis && (
                        <div>
                          <h4 className="text-xs font-medium text-gray-500 uppercase mb-1">Diagnosis</h4>
                          <div className="text-sm text-gray-800 leading-relaxed">
                            <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                              {session.diagnosis}
                            </Badge>
                          </div>
                        </div>
                      )}
                      
                      {/* Vitals */}
                      {(session.height || session.weight || session.bp_systolic || session.bp_diastolic || session.pulse || session.temperature || session.blood_pressure) && (
                        <div>
                          <h4 className="text-xs font-medium text-gray-500 uppercase mb-1">Vitals</h4>
                          <div className="space-y-1">
                            <p className="text-sm text-gray-800">
                              {session.temperature && <span>Temp: {session.temperature}°F</span>}
                              {session.temperature && session.pulse && <span> | </span>}
                              {session.pulse && <span>Pulse: {session.pulse} bpm</span>}
                              {(session.pulse || session.temperature) && session.spo2 && <span> | </span>}
                              {session.spo2 && <span>SpO2: {session.spo2}%</span>}
                              {(session.spo2 || session.pulse || session.temperature) && (session.blood_pressure || (session.bp_systolic && session.bp_diastolic)) && <span> | </span>}
                              {session.blood_pressure ? <span>BP: {session.blood_pressure}</span> : 
                                (session.bp_systolic && session.bp_diastolic && <span>BP: {session.bp_systolic}/{session.bp_diastolic}</span>)}
                            </p>
                            <p className="text-sm text-gray-800">
                              {session.height && <span>Height: {session.height} cm</span>}
                              {session.height && session.weight && <span> | </span>}
                              {session.weight && <span>Weight: {session.weight} kg</span>}
                              {(() => {
                                const bmiResult = getSessionBMI(session, baby);
                                if (bmiResult && bmiResult.bmi) {
                                  return (
                                    <>
                                      <span> | </span>
                                      <span>BMI: {bmiResult.bmi} </span>
                                      <Badge className={`${getBMIBadgeClasses(bmiResult.color)} border text-xs ml-1`}>
                                        {bmiResult.status}
                                      </Badge>
                                    </>
                                  );
                                }
                                return null;
                              })()}
                            </p>
                          </div>
                        </div>
                      )}
                      
                      {/* Temperature */}
                      {session.temperature && (
                        <div>
                          <h4 className="text-xs font-medium text-gray-500 uppercase mb-1">Temperature</h4>
                          <p className="text-sm text-gray-800">{session.temperature}°F</p>
                        </div>
                      )}
                      
                      {/* Tests */}
                      {session.tests && (
                        <div>
                          <h4 className="text-xs font-medium text-gray-500 uppercase mb-1">Tests Recommended</h4>
                          <p className="text-sm text-gray-800 leading-relaxed">{session.tests}</p>
                        </div>
                      )}
                      
                      {/* Medication Prescribed */}
                      {session.medication && (
                        <div>
                          <h4 className="text-xs font-medium text-gray-500 uppercase mb-1 flex items-center space-x-1">
                            <Pill className="w-3 h-3" />
                            <span>Medication Prescribed</span>
                          </h4>
                          <div className="text-sm text-gray-800 leading-relaxed space-y-1">
                            {session.medication.split(' | ').map((med, idx) => {
                              // Extract medication type if present
                              const typeMatch = med.match(/\[Type:\s*([^\]]+)\]/);
                              const medicationType = typeMatch ? typeMatch[1].trim().toLowerCase() : null;
                              // Clean the medication string for display
                              const cleanMed = med.replace(/\[Type:\s*[^\]]+\]/g, '').trim();
                              
                              return (
                                <div key={idx} className="flex items-start gap-2">
                                  {medicationType && (
                                    <MedicationIcon 
                                      type={medicationType} 
                                      size={16} 
                                      className="mt-0.5 flex-shrink-0" 
                                    />
                                  )}
                                  <span className="flex-1">{cleanMed}</span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      
                      {/* Notes */}
                      {session.notes && (
                        <div>
                          <h4 className="text-xs font-medium text-gray-500 uppercase mb-1">Additional Notes</h4>
                          <p className="text-sm text-gray-600 leading-relaxed italic">{session.notes}</p>
                        </div>
                      )}

                      {/* Display uploaded documents and test reports */}
                      {((session.documents && session.documents.length > 0) || (sessionReports[session.id] && sessionReports[session.id].length > 0)) && (
                        <div>
                          <h4 className="text-xs font-medium text-gray-500 uppercase mb-2 flex items-center space-x-1">
                            <FileText className="w-3 h-3" />
                            <span>Attached Documents & Reports</span>
                          </h4>
                          <div className="flex flex-wrap gap-2">
                            {/* Show existing documents */}
                            {session.documents && session.documents.map((doc, index) => {
                              const docUrl = getDocumentUrl(doc);
                              const filename = getFilename(doc);
                              const displayFilename = filename.length > 25 ? filename.substring(0, 25) + '...' : filename;
                              const uploadedBy = typeof doc === 'object' ? doc.uploaded_by : 'parent';
                              const canDelete = !viewOnlyMode && uploadedBy === 'parent';
                              
                              return (
                                <div key={index} className="flex items-center space-x-1 px-3 py-2 bg-gray-100 rounded-lg max-w-xs">
                                  <a
                                    href={`${BACKEND_URL}${docUrl}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    className="flex items-center space-x-2 hover:opacity-80 transition-opacity"
                                    download={isDICOMFile(docUrl)}
                                    title={filename}
                                  >
                                    <span className="text-lg flex-shrink-0">{getFileIcon(docUrl)}</span>
                                    <span className="text-xs text-gray-700">{displayFilename}</span>
                                  </a>
                                  {canDelete && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteDocumentClick(session.id, index, doc);
                                      }}
                                      className="flex-shrink-0 p-1 hover:bg-red-100 rounded transition-colors"
                                      title="Delete document"
                                    >
                                      <Trash2 className="w-3.5 h-3.5 text-red-500" />
                                    </button>
                                  )}
                                </div>
                              );
                            })}
                            {/* Show uploaded test reports from Reports Upload page */}
                            {sessionReports[session.id] && sessionReports[session.id].map((report) => (
                              <button
                                key={`report-${report.id}`}
                                onClick={(e) => { e.stopPropagation(); openFileWithPresignedUrl(report.report_url); }}
                                className="flex items-center space-x-2 px-3 py-2 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg transition-colors max-w-xs cursor-pointer"
                                title={`${report.test_name} - ${report.notes || 'Report'}`}
                              >
                                <FileText className="w-4 h-4 text-blue-600 flex-shrink-0" />
                                <div className="flex flex-col min-w-0">
                                  <span className="text-xs text-blue-900 font-medium truncate">{report.test_name}</span>
                                  {report.notes && <span className="text-xs text-blue-700 truncate">{report.notes}</span>}
                                </div>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Updated By */}
                      {session.updated_by && (session.formatted_doctor_name || session.doctor_name) && (
                        <p className="text-xs text-purple-600 mt-2">
                          Updated by {ensureDrPrefix(session.formatted_doctor_name || session.doctor_name)}
                          {session.hospital_name && ` - ${session.hospital_name}`}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Add Medical Session Dialog */}
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogContent className="sm:max-w-2xl z-50 max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Add Medical Session</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAddSession} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="session_date">Session Date *</Label>
                  <Input
                    id="session_date"
                    type="text"
                    placeholder="DD/MM/YYYY"
                    value={formData.session_date}
                    onChange={handleDateInput}
                    onKeyDown={handleDateKeyDown}
                    maxLength={10}
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="doctor_name">Doctor Name *</Label>
                  <Input
                    id="doctor_name"
                    placeholder="e.g., Dr. Akhil (Pediatrician)"
                    value={formData.doctor_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, doctor_name: e.target.value }))}
                    required
                  />
                </div>
              </div>
              
              {/* Follow-up Selection */}
              {sessions.length > 0 && (
                <div className="space-y-2">
                  <Label htmlFor="follow_up_of">Follow-up of Previous Session (Optional)</Label>
                  <select
                    id="follow_up_of"
                    value={formData.follow_up_of}
                    onChange={(e) => setFormData(prev => ({ ...prev, follow_up_of: e.target.value }))}
                    className="w-full h-10 px-3 rounded-md border border-gray-300 bg-white text-sm"
                  >
                    <option value="">Not a follow-up</option>
                    {sessions.map((session) => (
                      <option key={session.id} value={session.id}>
                        {formatDate(session.session_date)} - {session.doctor_name} ({session.diagnosis || 'No diagnosis'})
                      </option>
                    ))}
                  </select>
                </div>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="symptoms">Symptoms *</Label>
                <Textarea
                  id="symptoms"
                  placeholder="Describe the symptoms observed..."
                  value={formData.symptoms}
                  onChange={(e) => setFormData(prev => ({ ...prev, symptoms: e.target.value }))}
                  rows={3}
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="diagnosis">Diagnosis</Label>
                <Input
                  id="diagnosis"
                  placeholder="Enter the diagnosis (optional)"
                  value={formData.diagnosis}
                  onChange={(e) => setFormData(prev => ({ ...prev, diagnosis: e.target.value }))}
                />
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center justify-between mb-2">
                  <Label>Medications Prescribed</Label>
                  <Button 
                    type="button"
                    variant="outline" 
                    size="sm"
                    onClick={handleAddMedication}
                    className="text-blue-600"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add Medication
                  </Button>
                </div>
                
                {formData.medications.map((medication, index) => (
                  <div key={index} className="space-y-2">
                    <div className="flex flex-col sm:flex-row gap-2 items-start">
                      <div className="w-full sm:flex-1 space-y-2">
                        <Input
                          placeholder="Medication name"
                          value={medication.name}
                          onChange={(e) => handleMedicationChange(index, 'name', e.target.value)}
                        />
                        {/* Time of Day Selector */}
                        <div className="flex items-center gap-2 mt-1">
                          {['Morning', 'Afternoon', 'Evening', 'Night'].map((timeSlot) => {
                            const isSelected = medication.time_of_day?.includes(timeSlot);
                            return (
                              <button
                                key={timeSlot}
                                type="button"
                                onClick={() => {
                                  const currentTimes = medication.time_of_day || [];
                                  const newTimes = isSelected
                                    ? currentTimes.filter(t => t !== timeSlot)
                                    : [...currentTimes, timeSlot];
                                  handleMedicationChange(index, 'time_of_day', newTimes);
                                }}
                                className="flex flex-col items-center gap-1 group"
                              >
                                <div className={`w-2 h-2 rounded-full transition-all ${
                                  isSelected 
                                    ? 'bg-purple-600 scale-125' 
                                    : 'bg-gray-300 group-hover:bg-purple-400'
                                }`} />
                                <span className={`text-[10px] transition-colors ${
                                  isSelected 
                                    ? 'text-purple-600 font-medium' 
                                    : 'text-gray-500 group-hover:text-purple-500'
                                }`}>
                                  {timeSlot.substring(0, 1)}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                      {/* Timing and Duration - Side by side on mobile, single row on desktop */}
                      <div className="w-full sm:w-auto flex gap-2">
                        <div className="flex-1 sm:w-40">
                          <select
                            value={medication.timing}
                            onChange={(e) => handleMedicationChange(index, 'timing', e.target.value)}
                            className="w-full h-10 px-3 rounded-md border border-gray-300 bg-white text-sm"
                          >
                            {medicationTimings.map(timing => (
                              <option key={timing} value={timing}>{timing}</option>
                            ))}
                          </select>
                        </div>
                        <div className="flex-1 sm:w-32">
                          <Input
                            placeholder="Duration"
                            value={medication.duration}
                            onChange={(e) => handleMedicationChange(index, 'duration', e.target.value)}
                            className="w-full h-10"
                          />
                        </div>
                      </div>
                      {formData.medications.length > 1 && (
                        <Button 
                          type="button"
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleRemoveMedication(index)}
                          className="text-red-600 sm:mt-0 self-start"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="temperature">Temperature</Label>
                  <Input
                    id="temperature"
                    type="number"
                    step="0.1"
                    placeholder="37.0"
                    value={formData.temperature}
                    onChange={(e) => setFormData(prev => ({ ...prev, temperature: e.target.value }))}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="duration">Duration</Label>
                  <Input
                    id="duration"
                    type="text"
                    placeholder="e.g., 30 minutes, 1 hour"
                    value={formData.duration}
                    onChange={(e) => setFormData(prev => ({ ...prev, duration: e.target.value }))}
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="notes">Additional Notes</Label>
                <Textarea
                  id="notes"
                  placeholder="Any additional information..."
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  rows={3}
                />
              </div>
              
              {/* File Upload Section */}
              <div className="space-y-2">
                <Label>Attach Reports (PDF, Images)</Label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                  <input
                    type="file"
                    id="file-upload-add"
                    multiple
                    accept=".pdf,.jpg,.jpeg,.png,.gif,.bmp"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  <label htmlFor="file-upload-add" className="cursor-pointer">
                    <div className="flex flex-col items-center">
                      <Upload className="w-8 h-8 text-gray-400 mb-2" />
                      <p className="text-sm text-gray-600">Click to upload or drag and drop</p>
                      <p className="text-xs text-gray-500 mt-1">PDF, JPG, PNG (Multiple files supported)</p>
                    </div>
                  </label>
                  
                  {selectedFiles.length > 0 && (
                    <div className="mt-4 space-y-2">
                      <p className="text-sm font-medium text-gray-700">{selectedFiles.length} file(s) selected:</p>
                      {selectedFiles.map((file, index) => (
                        <div key={index} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded">
                          <div className="flex items-center space-x-2">
                            <FileText className="w-4 h-4 text-gray-600" />
                            <span className="text-sm text-gray-700">{file.name}</span>
                            <span className="text-xs text-gray-500">({(file.size / 1024).toFixed(1)} KB)</span>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveFile(index)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              
              <div className="flex space-x-2 pt-4">
                <Button 
                  type="submit" 
                  className="flex-1"
                  disabled={submitting}
                >
                  {submitting ? 'Saving...' : 'Save Session'}
                </Button>
                <Button 
                  type="button"
                  variant="outline" 
                  onClick={() => {
                    setShowAddDialog(false);
                    setFollowUpSessionId(null);
                    setSelectedFiles([]);
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit Medical Session Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="sm:max-w-2xl z-50 max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Medical Session</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleUpdateSession} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit_session_date">Session Date *</Label>
                  <Input
                    id="edit_session_date"
                    type="text"
                    placeholder="DD/MM/YYYY"
                    value={formData.session_date}
                    onChange={handleDateInput}
                    onKeyDown={handleDateKeyDown}
                    maxLength={10}
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="edit_doctor_name">Doctor Name *</Label>
                  <Input
                    id="edit_doctor_name"
                    placeholder="e.g., Dr. Akhil (Pediatrician)"
                    value={formData.doctor_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, doctor_name: e.target.value }))}
                    required
                  />
                </div>
              </div>
              
              {/* Follow-up Selection */}
              {sessions.length > 0 && (
                <div className="space-y-2">
                  <Label htmlFor="edit_follow_up_of">Follow-up of Previous Session (Optional)</Label>
                  <select
                    id="edit_follow_up_of"
                    value={formData.follow_up_of}
                    onChange={(e) => setFormData(prev => ({ ...prev, follow_up_of: e.target.value }))}
                    className="w-full h-10 px-3 rounded-md border border-gray-300 bg-white text-sm"
                  >
                    <option value="">Not a follow-up</option>
                    {sessions.filter(s => s.id !== editingSession?.id).map((session) => (
                      <option key={session.id} value={session.id}>
                        {formatDate(session.session_date)} - {session.doctor_name} ({session.diagnosis || 'No diagnosis'})
                      </option>
                    ))}
                  </select>
                </div>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="edit_symptoms">Symptoms *</Label>
                <Textarea
                  id="edit_symptoms"
                  placeholder="Describe the symptoms observed..."
                  value={formData.symptoms}
                  onChange={(e) => setFormData(prev => ({ ...prev, symptoms: e.target.value }))}
                  rows={3}
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="edit_diagnosis">Diagnosis</Label>
                <Input
                  id="edit_diagnosis"
                  placeholder="Enter the diagnosis (optional)"
                  value={formData.diagnosis}
                  onChange={(e) => setFormData(prev => ({ ...prev, diagnosis: e.target.value }))}
                />
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center justify-between mb-2">
                  <Label>Medications Prescribed</Label>
                  <Button 
                    type="button"
                    variant="outline" 
                    size="sm"
                    onClick={handleAddMedication}
                    className="text-blue-600"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add Medication
                  </Button>
                </div>
                
                {formData.medications.map((medication, index) => (
                  <div key={index} className="space-y-2">
                    <div className="flex flex-col sm:flex-row gap-2 items-start">
                      <div className="w-full sm:flex-1 space-y-2">
                        <Input
                          placeholder="Medication name"
                          value={medication.name}
                          onChange={(e) => handleMedicationChange(index, 'name', e.target.value)}
                        />
                        {/* Time of Day Selector */}
                        <div className="flex items-center gap-2 mt-1">
                          {['Morning', 'Afternoon', 'Evening', 'Night'].map((timeSlot) => {
                            const isSelected = medication.time_of_day?.includes(timeSlot);
                            return (
                              <button
                                key={timeSlot}
                                type="button"
                                onClick={() => {
                                  const currentTimes = medication.time_of_day || [];
                                  const newTimes = isSelected
                                    ? currentTimes.filter(t => t !== timeSlot)
                                    : [...currentTimes, timeSlot];
                                  handleMedicationChange(index, 'time_of_day', newTimes);
                                }}
                                className="flex flex-col items-center gap-1 group"
                              >
                                <div className={`w-2 h-2 rounded-full transition-all ${
                                  isSelected 
                                    ? 'bg-purple-600 scale-125' 
                                    : 'bg-gray-300 group-hover:bg-purple-400'
                                }`} />
                                <span className={`text-[10px] transition-colors ${
                                  isSelected 
                                    ? 'text-purple-600 font-medium' 
                                    : 'text-gray-500 group-hover:text-purple-500'
                                }`}>
                                  {timeSlot.substring(0, 1)}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                      {/* Timing and Duration - Side by side on mobile, single row on desktop */}
                      <div className="w-full sm:w-auto flex gap-2">
                        <div className="flex-1 sm:w-40">
                          <select
                            value={medication.timing}
                            onChange={(e) => handleMedicationChange(index, 'timing', e.target.value)}
                            className="w-full h-10 px-3 rounded-md border border-gray-300 bg-white text-sm"
                          >
                            {medicationTimings.map(timing => (
                              <option key={timing} value={timing}>{timing}</option>
                            ))}
                          </select>
                        </div>
                        <div className="flex-1 sm:w-32">
                          <Input
                            placeholder="Duration"
                            value={medication.duration}
                            onChange={(e) => handleMedicationChange(index, 'duration', e.target.value)}
                            className="w-full h-10"
                          />
                        </div>
                      </div>
                      {formData.medications.length > 1 && (
                        <Button 
                          type="button"
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleRemoveMedication(index)}
                          className="text-red-600 sm:mt-0 self-start"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit_temperature">Temperature</Label>
                  <Input
                    id="edit_temperature"
                    type="number"
                    step="0.1"
                    placeholder="37.0"
                    value={formData.temperature}
                    onChange={(e) => setFormData(prev => ({ ...prev, temperature: e.target.value }))}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="edit_duration">Duration</Label>
                  <Input
                    id="edit_duration"
                    type="text"
                    placeholder="e.g., 30 minutes, 1 hour"
                    value={formData.duration}
                    onChange={(e) => setFormData(prev => ({ ...prev, duration: e.target.value }))}
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="edit_notes">Additional Notes</Label>
                <Textarea
                  id="edit_notes"
                  placeholder="Any additional information..."
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  rows={3}
                />
              </div>
              
              <div className="flex space-x-2 pt-4">
                <Button 
                  type="submit" 
                  className="flex-1"
                  disabled={submitting}
                >
                  {submitting ? 'Updating...' : 'Update Session'}
                </Button>
                <Button 
                  type="button"
                  variant="outline" 
                  onClick={() => {
                    setShowEditDialog(false);
                    setEditingSession(null);
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Previous Session Dialog */}
        <Dialog open={showPreviousSessionDialog} onOpenChange={setShowPreviousSessionDialog}>
          <DialogContent className="sm:max-w-2xl z-50 max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Previous Session Details</DialogTitle>
            </DialogHeader>
            {previousSessionData && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-500">Session Date</Label>
                    <p className="font-semibold">{formatDate(previousSessionData.session_date)}</p>
                  </div>
                  <div>
                    <Label className="text-gray-500">Doctor Name</Label>
                    <p className="font-semibold">{previousSessionData.doctor_name}</p>
                  </div>
                </div>

                {previousSessionData.temperature && (
                  <div>
                    <Label className="text-gray-500">Temperature</Label>
                    <p className="font-semibold flex items-center space-x-1">
                      <Thermometer className="w-4 h-4 text-orange-600" />
                      <span>{previousSessionData.temperature}°C</span>
                    </p>
                  </div>
                )}

                <div>
                  <Label className="text-gray-500">Symptoms</Label>
                  <p className="text-gray-800">{previousSessionData.symptoms}</p>
                </div>

                {previousSessionData.diagnosis && (
                  <div>
                    <Label className="text-gray-500">Diagnosis</Label>
                    <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                      {previousSessionData.diagnosis}
                    </Badge>
                  </div>
                )}

                {previousSessionData.medication && (
                  <div>
                    <Label className="text-gray-500">Medication Prescribed</Label>
                    <p className="text-gray-800">{previousSessionData.medication}</p>
                  </div>
                )}

                {previousSessionData.notes && (
                  <div>
                    <Label className="text-gray-500">Additional Notes</Label>
                    <p className="text-gray-600 italic">{previousSessionData.notes}</p>
                  </div>
                )}

                {previousSessionData.documents && previousSessionData.documents.length > 0 && (
                  <div>
                    <Label className="text-gray-500">Attached Documents</Label>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {previousSessionData.documents.map((docUrl, index) => (
                        <a
                          key={index}
                          href={`${BACKEND_URL}${docUrl}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center space-x-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                        >
                          <span className="text-lg">{getFileIcon(docUrl)}</span>
                          <span className="text-xs text-gray-700">{getFilename(docUrl)}</span>
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex justify-end pt-4">
                  <Button 
                    variant="outline" 
                    onClick={() => setShowPreviousSessionDialog(false)}
                  >
                    Close
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Print Preview Modal */}
        <Dialog open={showPrintPreview} onOpenChange={setShowPrintPreview}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto print:max-w-full">
            <DialogHeader className="print:hidden">
              <DialogTitle>Prescription Preview</DialogTitle>
            </DialogHeader>
            
            {printSession && (
              <div id="prescription-print" className="bg-white p-8 print:p-0">
                {/* Print Styles */}
                <style>{`
                  @media print {
                    body * {
                      visibility: hidden;
                    }
                    #prescription-print, #prescription-print * {
                      visibility: visible;
                    }
                    #prescription-print {
                      position: absolute;
                      left: 0;
                      top: 0;
                      width: 100%;
                    }
                    @page {
                      margin: 0.5in;
                    }
                  }
                `}</style>

                {/* Top Section - Date and Doctor (Top Right) */}
                <div className="flex justify-end mb-6 text-sm">
                  <div className="text-right">
                    {printSession.hospital_name && (
                      <div className="mb-2 text-xl font-bold text-gray-800">
                        {printSession.hospital_name}
                      </div>
                    )}
                    <div className="mb-1">
                      <strong>Date:</strong> {formatDate(printSession.session_date)}
                    </div>
                    <div className="mb-1">
                      <span className="font-bold">{printSession.doctor_name}</span>
                    </div>
                  </div>
                </div>

                {/* Baby Info and QR Code - Parallel, Below Date/Doctor */}
                <div className="flex justify-between items-start mb-4 pb-4 border-b border-gray-300">
                  {/* Baby Information - Left Side */}
                  <div className="flex-1">
                    <div className="text-sm font-semibold mb-1 text-gray-700">
                      {baby.short_id || baby.id?.substring(0, 8).toUpperCase()}
                    </div>
                    <div className="text-lg font-bold mb-1">
                      {baby.name} ({(() => {
                        const gender = (baby.gender || baby.sex || '').toLowerCase();
                        if (gender === 'male' || gender === 'm' || gender === 'boy') return 'M';
                        if (gender === 'female' || gender === 'f' || gender === 'girl') return 'F';
                        return 'N/A';
                      })()})
                    </div>
                    <div className="text-sm text-gray-600">
                      <strong>Age:</strong> {(() => {
                        try {
                          let dob = baby.date_of_birth;
                          if (!dob) return 'N/A';
                          
                          // Ensure dob is a string
                          if (typeof dob !== 'string') {
                            dob = String(dob);
                          }
                          
                          let dobDate;
                          if (dob.includes('/')) {
                            const parts = dob.split('/');
                            const day = parseInt(parts[0], 10);
                            const month = parseInt(parts[1], 10);
                            const year = parseInt(parts[2], 10);
                            
                            // Validate parsed values
                            if (isNaN(day) || isNaN(month) || isNaN(year)) {
                              console.error('Invalid date parts:', { day, month, year, original: dob });
                              return 'N/A';
                            }
                            
                            dobDate = new Date(year, month - 1, day);
                          } else if (dob.includes('-')) {
                            const parts = dob.split('-');
                            const nums = parts.map(s => parseInt(s, 10));
                            
                            // Check for NaN in parsed values
                            if (nums.some(n => isNaN(n))) {
                              console.error('Invalid date parts:', nums, 'original:', dob);
                              return 'N/A';
                            }
                            
                            if (nums[0] > 1000) {
                              dobDate = new Date(nums[0], nums[1] - 1, nums[2]);
                            } else {
                              dobDate = new Date(nums[2], nums[1] - 1, nums[0]);
                            }
                          } else {
                            dobDate = new Date(dob);
                          }
                          
                          if (!dobDate || isNaN(dobDate.getTime())) {
                            console.error('Invalid date object:', dob, dobDate);
                            return 'N/A';
                          }
                          
                          const today = new Date();
                          const months = (today.getFullYear() - dobDate.getFullYear()) * 12 + today.getMonth() - dobDate.getMonth();
                          
                          if (isNaN(months) || months < 0) {
                            console.error('Invalid months calculation:', months, 'DOB:', dobDate, 'Today:', today);
                            return 'N/A';
                          }
                          if (months < 12) return `${months} month${months !== 1 ? 's' : ''}`;
                          
                          const years = Math.floor(months / 12);
                          const remainingMonths = months % 12;
                          return `${years} year${years > 1 ? 's' : ''}${remainingMonths > 0 ? ` ${remainingMonths} month${remainingMonths > 1 ? 's' : ''}` : ''}`;
                        } catch (error) {
                          console.error('Age calculation error:', error, 'Baby object:', baby);
                          return 'N/A';
                        }
                      })()}
                    </div>
                  </div>
                  
                  {/* QR Code - Right Side, parallel to baby info */}
                  <div className="ml-8">
                    <QRCodeSVG 
                      value={JSON.stringify({
                        sessionId: printSession.id,
                        babyId: baby.id,
                        babyName: baby.name,
                        date: printSession.session_date,
                        doctor: printSession.doctor_name,
                        diagnosis: printSession.diagnosis,
                        medication: printSession.medication
                      })}
                      size={100}
                      level="M"
                    />
                  </div>
                </div>

                {/* Prescription Symbol */}
                <div className="text-4xl font-serif mb-4">℞</div>

                {/* Vitals */}
                {(printSession.temperature || printSession.pulse || printSession.spo2 || printSession.blood_pressure || printSession.bp_systolic || printSession.bp_diastolic || printSession.height || printSession.weight) && (
                  <div className="mb-4">
                    <div className="font-semibold mb-1">Vitals:</div>
                    <div className="text-sm space-y-1">
                      <div>
                        {printSession.temperature && <span>Temp: {printSession.temperature}°F</span>}
                        {printSession.temperature && printSession.pulse && <span> | </span>}
                        {printSession.pulse && <span>Pulse: {printSession.pulse} bpm</span>}
                        {(printSession.pulse || printSession.temperature) && printSession.spo2 && <span> | </span>}
                        {printSession.spo2 && <span>SpO2: {printSession.spo2}%</span>}
                        {(printSession.spo2 || printSession.pulse || printSession.temperature) && (printSession.blood_pressure || (printSession.bp_systolic && printSession.bp_diastolic)) && <span> | </span>}
                        {printSession.blood_pressure ? <span>BP: {printSession.blood_pressure}</span> : 
                          (printSession.bp_systolic && printSession.bp_diastolic && <span>BP: {printSession.bp_systolic}/{printSession.bp_diastolic}</span>)}
                      </div>
                      <div>
                        {printSession.height && <span>Height: {printSession.height} cm</span>}
                        {printSession.height && printSession.weight && <span> | </span>}
                        {printSession.weight && <span>Weight: {printSession.weight} kg</span>}
                        {(() => {
                          const bmiResult = getSessionBMI(printSession, baby);
                          if (bmiResult && bmiResult.bmi) {
                            return (
                              <>
                                <span> | </span>
                                <span>BMI: {bmiResult.bmi} ({bmiResult.status})</span>
                              </>
                            );
                          }
                          return null;
                        })()}
                      </div>
                    </div>
                  </div>
                )}

                {/* Chief Complaints */}
                {printSession.complaints && (
                  <div className="mb-4">
                    <div className="font-semibold mb-1">Chief Complaints:</div>
                    <div className="text-sm">{printSession.complaints}</div>
                  </div>
                )}

                {/* Symptoms */}
                {printSession.symptoms && (
                  <div className="mb-4">
                    <div className="font-semibold mb-1">Symptoms:</div>
                    <div className="text-sm">{printSession.symptoms}</div>
                  </div>
                )}

                {/* Tests */}
                {printSession.tests && (
                  <div className="mb-4">
                    <div className="font-semibold mb-1">Tests Recommended:</div>
                    <div className="text-sm">{printSession.tests}</div>
                  </div>
                )}

                {/* Diagnosis */}
                {printSession.diagnosis && (
                  <div className="mb-4">
                    <div className="font-semibold mb-1">Diagnosis:</div>
                    <div className="text-sm">{printSession.diagnosis}</div>
                  </div>
                )}

                {/* Medication Table */}
                <div className="mb-6">
                  <div className="font-semibold mb-2">Medications Prescribed:</div>
                  <table className="w-full border-collapse border border-gray-300">
                    <thead>
                      <tr className="bg-gray-100">
                        <th className="border border-gray-300 px-3 py-2 text-left">#</th>
                        <th className="border border-gray-300 px-3 py-2 text-left">Medicine Name</th>
                        <th className="border border-gray-300 px-3 py-2 text-left">Dosage</th>
                        <th className="border border-gray-300 px-3 py-2 text-left">Duration</th>
                      </tr>
                    </thead>
                    <tbody>
                      {printSession.medication && printSession.medication.split(' | ').map((med, index) => {
                        // Parse medication string
                        let name = med;
                        let medicationType = '';
                        let genericName = '';
                        let treatmentFor = '';
                        let dosage = '';
                        let duration = '';
                        
                        // Extract medication type if present
                        const typeMatch = name.match(/\[Type:\s*([^\]]+)\]/);
                        if (typeMatch) {
                          medicationType = typeMatch[1].trim().toLowerCase();
                          name = name.replace(typeMatch[0], '').trim();
                        }
                        
                        // Extract generic name if present
                        const genericMatch = name.match(/\[Generic:\s*([^\]]+)\]/);
                        if (genericMatch) {
                          genericName = genericMatch[1].trim();
                          name = name.replace(genericMatch[0], '').trim();
                        }
                        
                        // Extract treatment for if present
                        const treatmentMatch = name.match(/\[For:\s*([^\]]+)\]/);
                        if (treatmentMatch) {
                          treatmentFor = treatmentMatch[1].trim();
                          name = name.replace(treatmentMatch[0], '').trim();
                        }
                        
                        // Extract duration
                        const durationMatch = name.match(/for\s+(.+)$/i);
                        if (durationMatch) {
                          duration = durationMatch[1];
                          name = name.substring(0, durationMatch.index).trim();
                        }
                        
                        // Extract timing info
                        const timingMatch = name.match(/^(.+?)\s*\((.+?)\)(?:\s+-\s+(.+))?$/);
                        if (timingMatch) {
                          name = timingMatch[1];
                          const timing = timingMatch[2]; // Before Food / After Food
                          const timeOfDay = timingMatch[3] || ''; // Morning, Night, etc.
                          
                          // Parse selected times
                          const selectedTimes = timeOfDay ? timeOfDay.split(',').map(t => t.trim()) : [];
                          const isMorning = selectedTimes.includes('Morning');
                          const isAfternoon = selectedTimes.includes('Afternoon');
                          const isEvening = selectedTimes.includes('Evening');
                          const isNight = selectedTimes.includes('Night');
                          
                          // Create dosage with dots and timing
                          dosage = {
                            times: { morning: isMorning, afternoon: isAfternoon, evening: isEvening, night: isNight },
                            timing: timing
                          };
                        }
                        
                        return (
                          <tr key={index}>
                            <td className="border border-gray-300 px-3 py-2">{index + 1}</td>
                            <td className="border border-gray-300 px-3 py-2">
                              <div className="flex items-center gap-2">
                                {medicationType && (
                                  <MedicationIcon 
                                    type={medicationType} 
                                    size={18} 
                                    className="flex-shrink-0" 
                                  />
                                )}
                                <span>{name}</span>
                              </div>
                              {(genericName || treatmentFor) && (
                                <div className="text-xs text-gray-600 mt-0.5">
                                  {genericName && <span className="italic">Generic: {genericName}</span>}
                                  {genericName && treatmentFor && <span className="mx-1">•</span>}
                                  {treatmentFor && <span>For: {treatmentFor}</span>}
                                </div>
                              )}
                            </td>
                            <td className="border border-gray-300 px-3 py-2">
                              {typeof dosage === 'object' && dosage.times ? (
                                <div className="flex flex-col items-center">
                                  {/* Dots Row - Horizontal */}
                                  <div className="flex items-center justify-center gap-2.5 mb-0.5">
                                    <div 
                                      className={`w-3.5 h-3.5 rounded-full border border-gray-700 ${dosage.times.morning ? 'bg-gray-700' : 'bg-white'}`}
                                    />
                                    <div 
                                      className={`w-3.5 h-3.5 rounded-full border border-gray-700 ${dosage.times.afternoon ? 'bg-gray-700' : 'bg-white'}`}
                                    />
                                    <div 
                                      className={`w-3.5 h-3.5 rounded-full border border-gray-700 ${dosage.times.evening ? 'bg-gray-700' : 'bg-white'}`}
                                    />
                                    <div 
                                      className={`w-3.5 h-3.5 rounded-full border border-gray-700 ${dosage.times.night ? 'bg-gray-700' : 'bg-white'}`}
                                    />
                                  </div>
                                  {/* Labels Row - Horizontal */}
                                  <div className="flex items-center justify-center gap-2.5 text-[9px] font-medium text-gray-700">
                                    <span className="w-3.5 text-center">M</span>
                                    <span className="w-3.5 text-center">A</span>
                                    <span className="w-3.5 text-center">E</span>
                                    <span className="w-3.5 text-center">N</span>
                                  </div>
                                  {/* Timing (Before/After Food) */}
                                  <div className="text-xs text-center mt-1 font-medium">({dosage.timing})</div>
                                </div>
                              ) : (
                                dosage
                              )}
                            </td>
                            <td className="border border-gray-300 px-3 py-2">{duration}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Assessment */}
                {printSession.assessment && (
                  <div className="mb-4">
                    <div className="font-semibold mb-1">Assessment:</div>
                    <div className="text-sm">{printSession.assessment}</div>
                  </div>
                )}

                {/* Notes */}
                {printSession.notes && (
                  <div className="mb-4">
                    <div className="font-semibold mb-1">Additional Notes:</div>
                    <div className="text-sm">{printSession.notes}</div>
                  </div>
                )}

                {/* Footer */}
                <div className="mt-8 pt-4 border-t text-xs text-gray-600 text-center">
                  <p>This is a computer-generated prescription</p>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 mt-4 print:hidden">
              <Button variant="outline" onClick={() => setShowPrintPreview(false)}>
                Close
              </Button>
              <Button onClick={handlePrint} className="bg-blue-600 hover:bg-blue-700">
                <Printer className="w-4 h-4 mr-2" />
                Print
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* View Session Details Dialog */}
        <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Medical Session Details</DialogTitle>
              <DialogDescription>
                View complete details of this medical session including vitals, diagnosis, and prescriptions.
              </DialogDescription>
            </DialogHeader>
            
            {viewSession && (
              <div className="space-y-4">
                {/* Date and Doctor */}
                <div className="flex items-center justify-between pb-4 border-b">
                  <div>
                    <p className="text-lg font-semibold">{formatDate(viewSession.session_date)}</p>
                    <p className="text-sm text-gray-600">{viewSession.doctor_name || 'Doctor'}</p>
                  </div>
                  {viewSession.updated_by && (
                    <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                      Doctor Session
                    </Badge>
                  )}
                </div>

                {/* Vitals */}
                {(viewSession.height || viewSession.weight || viewSession.bp_systolic || viewSession.bp_diastolic || viewSession.pulse) && (
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Vitals</h4>
                    <p className="text-sm text-gray-700">
                      {viewSession.height && <span>Height: {viewSession.height} cm</span>}
                      {viewSession.height && viewSession.weight && <span> | </span>}
                      {viewSession.weight && <span>Weight: {viewSession.weight} kg</span>}
                      {(viewSession.weight || viewSession.height) && (viewSession.bp_systolic || viewSession.pulse) && <span> | </span>}
                      {(viewSession.bp_systolic && viewSession.bp_diastolic) && <span>BP: {viewSession.bp_systolic}/{viewSession.bp_diastolic}</span>}
                      {(viewSession.bp_systolic && viewSession.bp_diastolic) && viewSession.pulse && <span> | </span>}
                      {viewSession.pulse && <span>Pulse: {viewSession.pulse} bpm</span>}
                    </p>
                  </div>
                )}

                {/* Temperature */}
                {viewSession.temperature && (
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Temperature</h4>
                    <p className="text-sm text-gray-700">{viewSession.temperature}°F</p>
                  </div>
                )}

                {/* Symptoms */}
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Symptoms</h4>
                  <p className="text-sm text-gray-700">{viewSession.symptoms || viewSession.chief_complaint}</p>
                </div>

                {/* Tests */}
                {viewSession.tests && (
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Tests Recommended</h4>
                    <p className="text-sm text-gray-700">{viewSession.tests}</p>
                  </div>
                )}

                {/* Diagnosis */}
                {viewSession.diagnosis && (
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Diagnosis</h4>
                    <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                      {viewSession.diagnosis}
                    </Badge>
                  </div>
                )}

                {/* Medication */}
                {viewSession.medication && (
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Medication Prescribed</h4>
                    <div className="text-sm text-gray-700 space-y-1">
                      {viewSession.medication.split(' | ').map((med, idx) => (
                        <div key={idx} className="flex items-start">
                          <span className="text-gray-400 mr-2">{idx + 1}.</span>
                          <span>{med}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Notes */}
                {viewSession.notes && (
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Notes</h4>
                    <p className="text-sm text-gray-700">{viewSession.notes}</p>
                  </div>
                )}

                {/* Documents and Test Reports */}
                {((viewSession.documents && viewSession.documents.length > 0) || (sessionReports[viewSession.id] && sessionReports[viewSession.id].length > 0)) && (
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2 flex items-center space-x-2">
                      <FileText className="w-4 h-4" />
                      <span>Attached Documents & Reports</span>
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {/* Show existing documents */}
                      {viewSession.documents && viewSession.documents.map((doc, index) => {
                        const docUrl = getDocumentUrl(doc);
                        const filename = getFilename(doc);
                        const displayFilename = filename.length > 25 ? filename.substring(0, 25) + '...' : filename;
                        const uploadedBy = typeof doc === 'object' ? doc.uploaded_by : 'parent';
                        const canDelete = !viewOnlyMode && uploadedBy === 'parent';
                        
                        return (
                          <div key={index} className="flex items-center space-x-1 px-3 py-2 bg-gray-100 rounded-lg max-w-md">
                            <a
                              href={`${BACKEND_URL}${docUrl}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center space-x-2 hover:opacity-80 transition-opacity"
                              download={isDICOMFile(docUrl)}
                              title={filename}
                            >
                              <span className="text-lg flex-shrink-0">{getFileIcon(docUrl)}</span>
                              <span className="text-sm text-gray-700">{displayFilename}</span>
                            </a>
                            {canDelete && (
                              <button
                                onClick={() => handleDeleteDocumentClick(viewSession.id, index, doc)}
                                className="flex-shrink-0 p-1 hover:bg-red-100 rounded transition-colors"
                                title="Delete document"
                              >
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </button>
                            )}
                          </div>
                        );
                      })}
                      {/* Show uploaded test reports from Reports Upload page */}
                      {sessionReports[viewSession.id] && sessionReports[viewSession.id].map((report) => (
                        <button
                          key={`report-${report.id}`}
                          onClick={() => openFileWithPresignedUrl(report.report_url)}
                          className="flex items-center space-x-2 px-3 py-2 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg transition-colors max-w-md cursor-pointer"
                          title={`${report.test_name} - ${report.notes || 'Report'}`}
                        >
                          <FileText className="w-4 h-4 text-blue-600 flex-shrink-0" />
                          <div className="flex flex-col min-w-0">
                            <span className="text-sm text-blue-900 font-medium truncate">{report.test_name}</span>
                            {report.notes && <span className="text-xs text-blue-700 truncate">{report.notes}</span>}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Updated By */}
                {viewSession.updated_by && (
                  <div className="pt-2 border-t">
                    <p className="text-xs text-purple-600">
                      Updated by {ensureDrPrefix(viewSession.formatted_doctor_name || viewSession.doctor_name)}
                      {viewSession.hospital_name && ` - ${viewSession.hospital_name}`}
                    </p>
                  </div>
                )}

                {/* Action Buttons - Only for parent-created sessions */}
                {!viewOnlyMode && !viewSession.updated_by && (
                  <div className="flex justify-end gap-2 pt-4 border-t">
                    <Button 
                      variant="outline" 
                      onClick={() => {
                        setShowViewDialog(false);
                        handleEditClick(viewSession);
                      }}
                    >
                      Edit Session
                    </Button>
                    <Button 
                      variant="destructive" 
                      onClick={() => {
                        setShowViewDialog(false);
                        handleDeleteSession(viewSession.id);
                      }}
                    >
                      Delete Session
                    </Button>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Delete Document Confirmation Dialog */}
        <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Delete Document</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete this document?
              </DialogDescription>
            </DialogHeader>
            <div className="flex justify-end space-x-2 mt-4">
              <Button
                type="button"
                variant="outline"
                onClick={handleCancelDeleteDocument}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="destructive"
                onClick={handleConfirmDeleteDocument}
              >
                Delete
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default MedicalSessions;
